import React, { useCallback, useRef, useState } from 'react';

import type { Command, EditorState } from 'prosemirror-state';
import type { IntlShape } from 'react-intl';

import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type {
	ExtractInjectionAPI,
	FloatingToolbarConfig,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types';
import type { Node as PMNode, NodeType, Schema } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarButton, ToolbarTooltip } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { InlineSuggestionToolbar } from '@atlassian/editor-plugin-ai/InlineSuggestionToolbar';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import {
	REMIX_DENY_LIST_V1,
	REMIX_EXPAND_NODE_TYPES,
} from '@atlassian/malleable-ui-components/remix/remix-config';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';

import type {
	MalleableUiPlugin,
	RemixHiddenOptionsResolver as RemixHiddenOptionsResolverType,
} from '../../malleableUiPluginType';
import { malleableUiPluginKey } from '../../pm-plugins/plugin-key';
import { remixThinkingPluginKey } from '../../pm-plugins/remix-thinking/remix-thinking-plugin';
import { EmptyRemixHiddenOptionsResolver } from '../components/RemixHiddenOptionsResolver';
import { messages } from '../messages';
import {
	ASK_ROVO_MEDIA_NODE_TYPES,
	isConsideredEmptyForRemix,
	isRemixInlineDropdownVariant,
	REMOVE_BACKGROUND_FLOATING_TOOLBAR_TEST_ID,
	REMIX_FLOATING_TOOLBAR_TEST_ID,
	REMIX_TOOLBAR_POPUP_OFFSET,
	shouldShowRemixForSelectionContext,
} from '../remix-config';
import {
	buildTypeSwitcherDropdownItems,
	getAskRovoTargetId,
} from './floating-toolbar/floating-toolbar-remix-variant';

const RemoveBackgroundIcon = () => <RovoIcon size="xxsmall" />;

export function getUnallowedNodes(schema: Schema): Set<NodeType> {
	return new Set(REMIX_DENY_LIST_V1.map((name) => schema.nodes[name]).filter(Boolean));
}

export function getMediaNodesForAskRovo(schema: Schema): Set<NodeType> {
	return new Set(ASK_ROVO_MEDIA_NODE_TYPES.map((name) => schema.nodes[name]).filter(Boolean));
}

const getMediaNodeForAskRovo = (node: PMNode): PMNode | undefined => {
	if (node.type.name === 'media') {
		return node;
	}
	if (node.type.name === 'mediaSingle') {
		return node.firstChild ?? undefined;
	}
	return undefined;
};

const isImageMediaNodeForAskRovo = (node: PMNode): boolean => {
	const mediaNode = getMediaNodeForAskRovo(node);
	if (!mediaNode || mediaNode.type.name !== 'media') {
		return false;
	}

	if (mediaNode.attrs?.type === 'external') {
		return true;
	}

	const fileMimeType = mediaNode.attrs?.__fileMimeType;
	return typeof fileMimeType === 'string' && fileMimeType.includes('image/');
};

const getMediaFileId = (node: PMNode): string | undefined => {
	const mediaFileId = getMediaNodeForAskRovo(node)?.attrs?.id;
	return typeof mediaFileId === 'string' && mediaFileId.length > 0 ? mediaFileId : undefined;
};

const remixFloatingToolbarClickAnalyticsPayload = {
	action: ACTION.CLICKED,
	actionSubject: ACTION_SUBJECT.BUTTON,
	actionSubjectId: 'remixModalFromToolbar',
	eventType: EVENT_TYPE.UI,
	source: 'remix',
} as const;

interface RemixFloatingToolbarButtonProps {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	intl: IntlShape;
	isDisabled: boolean;
	node: PMNode;
	remixHiddenOptionsResolver?: RemixHiddenOptionsResolverType;
	view: EditorView | undefined;
}

/**
 * Inline-dropdown variant of the floating-toolbar Remix item, rendered as a persistent React
 * component so the anchored picker can hang off a stable trigger. `openRemixModal` is only
 * dispatched once an option is picked — dispatching it just to show the picker moves the
 * ProseMirror selection, which makes the surrounding floating toolbar re-evaluate and detach
 * the popup from its trigger.
 */
function RemixFloatingToolbarButton({
	api,
	intl,
	isDisabled,
	node,
	remixHiddenOptionsResolver: RemixHiddenOptionsResolver = EmptyRemixHiddenOptionsResolver,
	view,
}: RemixFloatingToolbarButtonProps): React.JSX.Element {
	const { formatMessage } = intl;

	const { contentId, isRemixing } = useSharedPluginStateWithSelector(
		api,
		['contextIdentifier', 'malleableUi'],
		(states) => ({
			contentId: states.contextIdentifierState?.contextIdentifierProvider?.objectId,
			isRemixing:
				states.malleableUiState?.isRemixModalOpen ||
				false ||
				states.malleableUiState?.isThinking ||
				false,
		}),
	);
	const effectiveDisabled = isDisabled || isRemixing;

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	// Captured at click time so the remix targets the node the user clicked on rather than
	// whatever the selection happens to be once they finish picking an option.
	const nodePosRef = useRef<number | undefined>(undefined);

	const handleClick = useCallback(() => {
		if (effectiveDisabled || !view) {
			return;
		}
		expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
		expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

		const $pos = view.state.selection.$from;
		let nodePos: number | undefined;
		for (let depth = $pos.depth; depth > 0; depth--) {
			if ($pos.node(depth) === node) {
				nodePos = $pos.before(depth);
				break;
			}
		}
		nodePosRef.current = nodePos;
		api.analytics?.actions.fireAnalyticsEvent<
			typeof remixFloatingToolbarClickAnalyticsPayload,
			'customEventType'
		>(remixFloatingToolbarClickAnalyticsPayload);
		setIsDropdownOpen((open) => !open);
	}, [api, effectiveDisabled, node, view]);

	const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

	const handleOptionSelect = useCallback(
		({ type, subType, userPrompt }: RemixOptionSelectionParameters) => {
			closeDropdown();
			api.core.actions.execute(
				api.malleableUi.commands.openRemixModal({
					nodePos: nodePosRef.current,
					nodeType: node.type.name,
					metadata: {
						interactionSource: 'remixModalFromFloatingToolbar',
						preselectedType: type,
						preselectedSubType: subType,
						...(userPrompt && { preselectedUserPrompt: userPrompt }),
					},
					source: 'floatingToolbar',
				}),
			);
		},
		[api, closeDropdown, node],
	);

	const tooltipContent = isRemixing
		? formatMessage(messages.remixInProgressTooltip)
		: formatMessage(messages.remixToolbarTooltip);

	const renderTrigger = (triggerProps: TriggerProps) => (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButton
				{...triggerProps}
				iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
				testId={REMIX_FLOATING_TOOLBAR_TEST_ID}
				onClick={handleClick}
				isSelected={isDropdownOpen}
				isDisabled={effectiveDisabled}
			>
				{formatMessage(messages.remixToolbarLabel)}
			</ToolbarButton>
		</ToolbarTooltip>
	);

	return (
		<RemixHiddenOptionsResolver enabled contentId={contentId}>
			{(hiddenOptions) => (
				<RemixContainer
					hiddenOptions={hiddenOptions}
					presentation="popup"
					trigger={renderTrigger}
					popupPlacement="bottom-start"
					popupOffset={REMIX_TOOLBAR_POPUP_OFFSET}
					isPopupOpen={isDropdownOpen}
					isThinking={false}
					onClose={closeDropdown}
					onOptionSelect={handleOptionSelect}
				/>
			)}
		</RemixHiddenOptionsResolver>
	);
}

export function getOverriddenFloatingToolbarConfig(
	api?: ExtractInjectionAPI<MalleableUiPlugin>,
	options?: {
		remixHiddenOptionsResolver?: RemixHiddenOptionsResolverType;
		shouldHideToolbarWhileRemixing?: boolean;
	},
): (
	state: EditorState,
	intl: IntlShape,
	_providerFactory: unknown,
	activeConfigs?: Array<FloatingToolbarConfig>,
) => FloatingToolbarConfig | undefined {
	if (!api?.toolbar) {
		return () => undefined;
	}

	return (
		state: EditorState,
		intl: IntlShape,
		_providerFactory: unknown,
		activeConfigs?: Array<FloatingToolbarConfig>,
	): FloatingToolbarConfig | undefined => {
		if (!activeConfigs?.length) {
			return;
		}

		const unallowedNodes = getUnallowedNodes(state.schema);
		const malleableUiState = malleableUiPluginKey.getState(state);
		const remixThinkingState = remixThinkingPluginKey.getState(state);
		const isRemixing =
			malleableUiState?.isRemixModalOpen || false || remixThinkingState?.isThinking || false;
		const areAiEntryPointsDisabled =
			api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled ?? false;
		activeConfigs.forEach((config) => {
			const configNodeTypes = ([] as NodeType[]).concat(config.nodeType);
			const isTableConfig = !Array.isArray(config.nodeType) && config.nodeType.name === 'table';

			// When an element is being remixed (modal open or thinking bar showing), hide its
			// element/floating toolbar entirely by emptying its items so the floating toolbar plugin
			// won't show it.
			//
			// Behind `cc-maui-phase-3` this applies to every element type. Previously the toolbar
			// was only hidden for tables, which left the element toolbar visible for other nodes
			// (e.g. panels and column layouts) while the thinking bar was showing. We skip the
			// inline-suggestion toolbar so the in-editor remix loading bar itself is not affected.
			// When the gate is off we retain the legacy table-only behaviour below.
			//
			// `shouldHideToolbarWhileRemixing` is an explicit opt-in from the product (only
			// Confluence sets this to true). This replaces the previous `config.className !==
			// undefined` heuristic, which accidentally affected products (e.g. Jira) that don't
			// set a className on their element toolbar configs.
			if (
				isRemixing &&
				options?.shouldHideToolbarWhileRemixing &&
				config.className !== InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS &&
				fg('cc-maui-phase-3')
			) {
				config.items = [];
				return;
			}

			// Legacy behaviour (gate off): hide only the table element toolbar while remixing.
			if (isTableConfig && isRemixing && !fg('cc-maui-phase-3')) {
				config.items = [];
				return;
			}

			// Check if every config node type is denied or inline. If the config also
			// includes multiBodiedExtension we defer the final decision to the items
			// callback where we can inspect the actual node's extensionKey (so that
			// allowed MBE variants like native tabs still get the Remix button).
			const hasMBENodeType = configNodeTypes.some((nt) => nt.name === 'multiBodiedExtension');
			// Expands are deny-listed by default and only remixable under REMIX_EXPANDS_EXPERIMENT.
			// Keep their configs alive here (like MBE below) so the items callback can make the
			// gated decision against the real node — the experiment must not be read for every
			// element toolbar evaluation.
			const hasExpandNodeType = configNodeTypes.some((nt) =>
				REMIX_EXPAND_NODE_TYPES.includes(nt.name),
			);
			const allDeniedOrInline = configNodeTypes.every(
				(nodeType) => unallowedNodes.has(nodeType) || nodeType.isInline,
			);

			if (
				(config.className !== undefined &&
					config.className === InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS) ||
				(isTableConfig && config.items.length === 0) ||
				(configNodeTypes.some((nodeType) => unallowedNodes.has(nodeType) || nodeType.isInline) &&
					!hasMBENodeType &&
					!hasExpandNodeType)
			) {
				return;
			}

			// When all node types are denied/inline and the only reason we didn't
			// skip is because the config includes MBE or an expand, we need to gate the Remix
			// button on the actual node: an allowed MBE variant, or an expand with the
			// experiment enabled.
			const requiresNodeCheck = allDeniedOrInline && (hasMBENodeType || hasExpandNodeType);

			const toolbarItems = config.items;
			if (Array.isArray(toolbarItems)) {
				const remixButton: FloatingToolbarItem<Command> = {
					id: 'editor.malleable-ui.remix',
					type: 'button',
					title: intl.formatMessage(messages.remixToolbarLabel),
					tooltipContent: isRemixing
						? intl.formatMessage(messages.remixInProgressTooltip)
						: intl.formatMessage(messages.remixToolbarTooltip),
					showTitle: true,
					hideTooltipOnClick: false,
					icon: AiGenerativeRemixIcon,
					onClick: () => false,
					disabled: isRemixing || areAiEntryPointsDisabled,
					testId: REMIX_FLOATING_TOOLBAR_TEST_ID,
				};
				const remixToolbarItems: FloatingToolbarItem<Command>[] = [
					remixButton,
					{
						type: 'separator',
						fullHeight: true,
					},
				];

				config.items = (node) => {
					// When the config was only kept because it contains MBE or an expand, check
					// whether the actual selected node is remixable — an allowed MBE variant
					// (e.g. native tabs), or a non-empty expand with the experiment on. If not,
					// return the original items without the Remix button.
					if (requiresNodeCheck && !shouldShowRemixForSelectionContext({ node })) {
						return toolbarItems;
					}

					const isRemixButtonDisabled =
						isRemixing || areAiEntryPointsDisabled || isConsideredEmptyForRemix(node);

					if (isRemixInlineDropdownVariant()) {
						return [
							{
								type: 'custom',
								fallback: [],
								render: (view) => (
									<RemixFloatingToolbarButton
										api={api}
										intl={intl}
										isDisabled={isRemixButtonDisabled}
										node={node}
										remixHiddenOptionsResolver={options?.remixHiddenOptionsResolver}
										view={view}
									/>
								),
							},
							{
								type: 'separator',
								fullHeight: true,
							},
							...toolbarItems,
						];
					}

					remixButton.onClick = (editorState) => {
						if (isRemixing) {
							return false;
						}
						expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
						expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

						const $pos = editorState.selection.$from;
						let nodePos: number | undefined;
						for (let depth = $pos.depth; depth > 0; depth--) {
							if ($pos.node(depth) === node) {
								nodePos = $pos.before(depth);
								break;
							}
						}
						api.analytics?.actions.fireAnalyticsEvent<
							typeof remixFloatingToolbarClickAnalyticsPayload,
							'customEventType'
						>(remixFloatingToolbarClickAnalyticsPayload);
						api.core.actions.execute(
							api.malleableUi.commands.openRemixModal({
								nodePos,
								nodeType: node.type.name,
								metadata: {
									interactionSource: 'remixModalFromFloatingToolbar',
								},
								source: 'floatingToolbar',
							}),
						);
						return true;
					};
					remixButton.disabled = isRemixButtonDisabled;
					return [...remixToolbarItems, ...toolbarItems];
				};
			}
		});

		const mediaNodesForAskRovo = getMediaNodesForAskRovo(state.schema);
		const isViewMode = api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
		const showAskRovoForMedia =
			fg('cc-maui-phase-2') ||
			(isViewMode && expValEquals('cc-maui-experiment', 'isEnabled', true));
		if (!mediaNodesForAskRovo.size || (!showAskRovoForMedia && isViewMode)) {
			return;
		}

		const askRovoButton = showAskRovoForMedia
			? api?.aiExperience?.actions?.getAskRovoFloatingToolbarButton?.({
					intl,
					disabled:
						api?.malleableUi?.actions?.shouldDisableRemix?.() &&
						fg('platform_editor_ai_remix_toolbar'),
					target: isViewMode ? 'default' : 'image',
				})
			: undefined;

		const shouldShowAskRovoForMediaNode = (node: PMNode) =>
			mediaNodesForAskRovo.has(node.type) &&
			!node.type.isInline &&
			isImageMediaNodeForAskRovo(node);

		const buildTypeSwitcherItemsForMediaNode = (
			node: PMNode,
		): FloatingToolbarItem<Command> | undefined => {
			return buildTypeSwitcherDropdownItems({
				node,
				schema: state.schema,
				state,
				intl,
				disabled: Boolean(isRemixing || areAiEntryPointsDisabled),
				api,
			});
		};

		// Build a per-node wrapped Ask Rovo button whose onClick stages the
		// selected media's target id (localId, falling back to file id) as the
		// inline-Rovo streaming target.
		const buildAskRovoItemsForMediaNode = (node: PMNode): FloatingToolbarItem<Command>[] => {
			if (!askRovoButton) {
				return [];
			}
			const wrappedAskRovoButton: FloatingToolbarItem<Command> = {
				...askRovoButton,
				...(isViewMode ? { supportsViewMode: true } : {}),
				onClick: (innerState, dispatch, view) => {
					if (fg('cc-maui-phase-2')) {
						const targetId = getAskRovoTargetId(node);
						if (targetId) {
							api?.aiExperience?.actions?.stageInlineRovoStreaming?.(targetId);
						}
					}
					return askRovoButton.onClick ? askRovoButton.onClick(innerState, dispatch, view) : false;
				},
			};
			return [
				wrappedAskRovoButton,
				{
					type: 'separator',
					fullHeight: true,
					...(isViewMode ? { supportsViewMode: true } : {}),
				},
			];
		};

		const buildRemoveBackgroundItemsForMediaNode = (
			node: PMNode,
		): FloatingToolbarItem<Command>[] => {
			const mediaFileId = getMediaFileId(node);
			if (isViewMode || !mediaFileId || isRemixing || areAiEntryPointsDisabled) {
				return [];
			}
			if (!expVal('cc_image_remove_background', 'isEnabled', false)) {
				return [];
			}

			const label = intl.formatMessage(messages.removeBackgroundToolbarLabel);
			return [
				{
					id: 'editor.malleable-ui.remove-background',
					type: 'button',
					title: label,
					tooltipContent: label,
					showTitle: true,
					hideTooltipOnClick: false,
					icon: RemoveBackgroundIcon,
					disabled: false,
					testId: REMOVE_BACKGROUND_FLOATING_TOOLBAR_TEST_ID,
					onClick: () => false,
				},
				{ type: 'separator', fullHeight: true },
			];
		};

		activeConfigs.forEach((config) => {
			const nodeTypes = ([] as NodeType[]).concat(config.nodeType);
			const isMediaConfig = nodeTypes.some(
				(nodeType) => mediaNodesForAskRovo.has(nodeType) && !nodeType.isInline,
			);
			if (!isMediaConfig) {
				return;
			}

			const items = config.items;

			if (Array.isArray(items)) {
				config.items = (node) => {
					if (!shouldShowAskRovoForMediaNode(node)) {
						return items;
					}
					const withAskRovo = [
						...buildAskRovoItemsForMediaNode(node),
						...buildRemoveBackgroundItemsForMediaNode(node),
						...items,
					];
					if (fg('platform_editor_ai_remix_toolbar')) {
						const typeSwitcher = buildTypeSwitcherItemsForMediaNode(node);
						if (typeSwitcher) {
							withAskRovo.splice(1, 0, typeSwitcher);
						}
					}
					return withAskRovo;
				};
			} else if (typeof items === 'function') {
				config.items = (node) => {
					const baseItems = items(node);
					if (!shouldShowAskRovoForMediaNode(node)) {
						return baseItems;
					}
					const withAskRovo = [
						...buildAskRovoItemsForMediaNode(node),
						...buildRemoveBackgroundItemsForMediaNode(node),
						...baseItems,
					];
					if (fg('platform_editor_ai_remix_toolbar')) {
						const typeSwitcher = buildTypeSwitcherItemsForMediaNode(node);
						if (typeSwitcher) {
							withAskRovo.splice(1, 0, typeSwitcher);
						}
					}
					return withAskRovo;
				};
			}
		});
	};
}
